import { useEffect, useState, type CSSProperties } from 'react'
import { cn } from '../lib/cn'

export interface SkeletonProps {
  variant?: 'text' | 'block' | 'circle' | 'table' | 'stat' | 'key-value'
  /** 'text' */
  lines?: number
  /** 'table' */
  rows?: number
  /** 'table': Breiten in % */
  columns?: number[]
  width?: number | string
  height?: number | string
  /** Standard 150: schnelle Antworten zeigen gar kein Skelett */
  delayMs?: number
  className?: string
}

const LINE_WIDTHS = ['92%', '78%', '85%', '60%', '88%', '70%']

/** Platzhalter in den Maßen des späteren Inhalts, damit beim Eintreffen nichts springt. */
export function Skeleton({
  variant = 'text',
  lines = 3,
  rows = 5,
  columns = [12, 34, 14, 14, 14, 12],
  width,
  height,
  delayMs = 150,
  className,
}: SkeletonProps) {
  const [visible, setVisible] = useState(delayMs <= 0)
  useEffect(() => {
    if (delayMs <= 0) return
    const timer = setTimeout(() => setVisible(true), delayMs)
    return () => clearTimeout(timer)
  }, [delayMs])

  const style: CSSProperties = { width, height }
  const hidden = !visible && 'invisible'
  const a11y = { 'aria-hidden': true as const }

  if (variant === 'block')
    return <div {...a11y} className={cn('skeleton h-24 w-full rounded-md', hidden, className)} style={style} />
  if (variant === 'circle')
    return <div {...a11y} className={cn('skeleton size-8 rounded-full', hidden, className)} style={style} />
  if (variant === 'stat') {
    return (
      <div
        {...a11y}
        className={cn('flex flex-col gap-2 rounded-md border border-line bg-raised p-4', hidden, className)}
        style={style}
      >
        <div className="skeleton h-3 w-24" />
        <div className="skeleton h-8 w-36" />
        <div className="skeleton h-3 w-28" />
      </div>
    )
  }
  if (variant === 'key-value') {
    return (
      <div {...a11y} className={cn('grid grid-cols-2 gap-x-6 gap-y-4', hidden, className)} style={style}>
        {Array.from({ length: lines * 2 }, (_, index) => (
          <div key={index} className="flex flex-col gap-1.5">
            <div className="skeleton h-3 w-20" />
            <div className="skeleton h-4" style={{ width: LINE_WIDTHS[index % LINE_WIDTHS.length] }} />
          </div>
        ))}
      </div>
    )
  }
  if (variant === 'table') {
    return (
      <div {...a11y} className={cn('w-full', hidden, className)} style={style}>
        {Array.from({ length: rows }, (_, row) => (
          <div key={row} className="flex h-row items-center gap-4 border-b border-line-subtle px-cell">
            {columns.map((percent, column) => (
              <div key={column} style={{ width: `${percent}%` }}>
                <div className="skeleton h-3.5" style={{ width: LINE_WIDTHS[(row + column) % LINE_WIDTHS.length] }} />
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  }
  return (
    <div {...a11y} className={cn('flex w-full flex-col gap-2', hidden, className)} style={style}>
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          className="skeleton h-3.5"
          style={{ width: lines === 1 ? '100%' : LINE_WIDTHS[index % LINE_WIDTHS.length] }}
        />
      ))}
    </div>
  )
}
